import { ReactElement, useEffect, useRef } from 'react';
import { Box, DialogBody, HStack, Link, Text } from '@chakra-ui/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { ChevronRightIcon } from 'assets/icons/v2';
import { ArticleImage } from 'components/Article/ArticleImage';
import { renderOptions } from 'components/Article/Options';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { ModalKeywordVariantProps, SharedModalProps } from 'types/Modal';
import { ProtectedRoutes } from 'types/Routes';
import { Document } from '@contentful/rich-text-types';
import { EntryType, KeywordArticle } from '@novaheal/types';
import { CustomModal } from '../CustomModal';

type Props = SharedModalProps & ModalKeywordVariantProps;

export const KeywordModal = ({ handleClose, isShown, data: keyword }: Props): ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const ref = useRef<HTMLDivElement>(null);
  const getBaseRoute = (relatedArticle: KeywordArticle): string => {
    return relatedArticle.entryType === EntryType.ARTICLE
      ? ProtectedRoutes.ARTICLE
      : ProtectedRoutes.MEDICATION;
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, keyword]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!keyword) return <></>;

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      size="md"
      placement={isDesktop ? 'center' : 'bottom'}
      borderRadius={isDesktop ? undefined : '10px 10px 0 0'}
      title={keyword.title}
    >
      <DialogBody ref={ref} p={{ base: '15px 20px 40px', md: '25px 20px' }} scrollbar="hidden">
        {keyword.metaInformation && (
          <Box position="relative" mb="8px" mt="6" width="100%">
            <Box
              height="100%"
              w="4px"
              borderRadius="full"
              bg="icon.info"
              position="absolute"
              left="0"
            />
            <Text textStyle="body.s.regular" position="relative" left="12px">
              {keyword.metaInformation}
            </Text>
          </Box>
        )}

        {documentToReactComponents(
          keyword.content.json as Document,
          renderOptions(keyword.content.links, 'keyword')
        )}

        {keyword.image && (
          <ArticleImage
            boxSize="65px"
            imgUrl={keyword.image.url}
            align="left"
            description={keyword.image.description}
          />
        )}

        {keyword.relatedArticle && (
          <Link
            href={`${getBaseRoute(keyword.relatedArticle)}/${keyword.relatedArticle.id}`}
            _focusWithin={{ outline: 'none' }}
          >
            <HStack
              border="default"
              borderColor="text.accent"
              borderRadius="10px"
              p="4"
              mt="4"
              justifyContent="space-between"
              color="text.accent"
            >
              <Text textStyle="body.s.regular">{keyword.relatedArticle.title}</Text>
              <ChevronRightIcon w="20px" h="20px" />
            </HStack>
          </Link>
        )}
      </DialogBody>
    </CustomModal>
  );
};
